import { type ConfrontoCopa } from './copa';
import { fasesDeMataMata } from './libertadores';
import type { EstadoJogo, PartidaJogada } from './jogo';

/**
 * Relógio compartilhado da rodada online. O servidor guarda só quanto tempo de jogo já passou (`decorridoMs`);
 * cada cliente transforma esse número em minuto, suspense de pênalti e disputa usando a MESMA linha do tempo,
 * calculada dos resultados da rodada. Pausar congela o número para todos.
 */

/** ms de relógio por minuto de jogo (a rodada inteira dura ~25 s); o servidor pode usar outro valor (RITMO_MS no ambiente). */
export const RITMO_MS = 250;

/** O botão "Pausar para substituir" fica escondido por enquanto; o servidor e o código da pausa continuam prontos. */
export const PAUSA_NA_TELA = false;
export const PENALTI_TENSAO_MS = 3200;
export const PENALTI_RESULTADO_MS = 1700;
export const COBRANCA_TENSAO_MS = 1800;
export const COBRANCA_RESULTADO_MS = 1300;
export const COBRANCA_MS = COBRANCA_TENSAO_MS + COBRANCA_RESULTADO_MS;

export const PAUSA_MS = 30_000;
export const PAUSAS_POR_JOGO = 3;

/** Intervalo online: o relógio para no fim do minuto 45 e só volta quando todos confirmam ou o tempo acaba. */
export const INTERVALO_MS = 45_000;
export const INTERVALO_MINUTO = 45;

/** Pausa para hidratação (online): aos 65' o relógio para para todos os técnicos, que podem ajustar o time, e volta quando todos confirmam ou o tempo acaba. */
export const HIDRATACAO_MS = 20_000;
export const HIDRATACAO_MINUTO = 65;

export interface IntervaloRodada {
  /** tempo de jogo (ms) em que o relógio para (fim do minuto 45) */
  em: number;
  /** técnicos humanos que jogam esta rodada (todos precisam confirmar) */
  esperados: number[];
  prontos: number[];
  /** instante do servidor em que o tempo do intervalo acaba (null = o relógio ainda não chegou lá) */
  ate: number | null;
  /** já terminou: o relógio voltou a andar */
  feito: boolean;
}

/** Estado do relógio guardado na sala (e enviado aos clientes). */
export interface RelogioRodada {
  /** tempo de jogo já decorrido quando o relógio foi (re)iniciado pela última vez */
  baseMs: number;
  /** instante do servidor em que ele foi (re)iniciado */
  inicio: number;
  /** ms por minuto de jogo nesta rodada (o servidor manda; todos usam o mesmo) */
  ritmo?: number;
  /** pausa em andamento: de quem, em que minuto e até quando o servidor espera */
  pausa: { clubeId: number; nome: string; minuto: number; ate: number } | null;
  /** pausas já usadas por clube nesta rodada */
  usadas: Record<number, number>;
  intervalo?: IntervaloRodada | null;
  /** pausa para hidratação aos 65' (mesma estrutura do intervalo); ausente em rodadas de salas antigas */
  hidratacao?: IntervaloRodada | null;
}

/** Tempo decorrido sem considerar o intervalo (o servidor usa para saber quando o relógio chegou nele). */
export function decorridoBrutoMs(r: RelogioRodada, agora: number): number {
  if (!r.pausa) return r.baseMs + Math.max(0, agora - r.inicio);
  return r.baseMs + Math.max(0, agora - r.pausa.ate);
}

/** Tempo de jogo decorrido no instante `agora` (do servidor). Se a pausa já venceu, o relógio voltou a andar; no intervalo ele fica parado. */
export function decorridoMs(r: RelogioRodada, agora: number): number {
  const bruto = decorridoBrutoMs(r, agora);
  if (r.intervalo && !r.intervalo.feito) return Math.min(bruto, r.intervalo.em);
  if (r.hidratacao && !r.hidratacao.feito) return Math.min(bruto, r.hidratacao.em);
  return bruto;
}

/** Disputa de pênaltis (Copa) decidida nesta partida, se houve. */
export function disputaDaPartida(e: EstadoJogo, rodada: number, mandante: number, visitante: number): ConfrontoCopa | undefined {
  const fase = fasesDeMataMata(e).find((f) => f.rodadas[f.rodadas.length - 1] === rodada);
  return fase?.confrontos.find((c) => c.cobrancas && ((c.a === mandante && c.b === visitante) || (c.a === visitante && c.b === mandante)));
}

export interface LinhaTempo {
  /** maior número de minutos entre as partidas da rodada */
  max: number;
  /** inicios[m] = ms em que o minuto m começa (m de 1 a max); inicios[max + 1] = fim da rodada */
  inicios: number[];
  /** ms extras no começo do minuto m (suspense de pênalti ou disputa) */
  extras: number[];
  fim: number;
}

/**
 * Cada minuto dura RITMO_MS, mais o suspense de pênalti (se alguma partida tem pênalti nele) ou a disputa de
 * pênaltis (no último minuto da partida decidida por ela). Todos os clientes e o servidor chegam à mesma linha.
 */
export function montarLinhaTempo(e: EstadoJogo, rodada: number, partidas: PartidaJogada[], ritmo: number = RITMO_MS): LinhaTempo {
  const max = Math.max(1, ...partidas.map((p) => p.resultado.totalMinutos));
  const extras = new Array<number>(max + 2).fill(0);
  for (const p of partidas) {
    for (const ev of p.resultado.eventos) {
      if (ev.tipo === 'penalti') extras[ev.minuto] = Math.max(extras[ev.minuto] ?? 0, PENALTI_TENSAO_MS + PENALTI_RESULTADO_MS);
    }
    const disputa = disputaDaPartida(e, rodada, p.mandante, p.visitante);
    if (disputa?.cobrancas) {
      const t = p.resultado.totalMinutos;
      extras[t] = Math.max(extras[t] ?? 0, disputa.cobrancas.length * COBRANCA_MS + 800);
    }
  }
  const inicios = new Array<number>(max + 2).fill(0);
  let t = 0;
  for (let m = 1; m <= max; m++) {
    inicios[m] = t;
    t += ritmo + (extras[m] ?? 0);
  }
  inicios[max + 1] = t;
  return { max, inicios, extras, fim: t };
}

/** Minuto (1..max) em que o relógio está; 0 antes de começar. */
export function minutoEm(l: LinhaTempo, decorrido: number): number {
  if (decorrido < 0) return 0;
  if (decorrido >= l.fim) return l.max;
  let lo = 1;
  let hi = l.max;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((l.inicios[mid] ?? 0) <= decorrido) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** O que o cliente deve mostrar da própria partida num dado instante. */
export interface EstadoDaPartida {
  minuto: number;
  fase: 'ao-vivo' | 'intervalo' | 'penaltis' | 'fim';
  /** pênalti em andamento (evento e etapa) */
  suspense?: { minuto: number; etapa: 'tensao' | 'resultado' };
  disputa?: { reveladas: number; etapa: 'tensao' | 'resultado' };
}

export function estadoDaPartida(e: EstadoJogo, rodada: number, p: PartidaJogada, l: LinhaTempo, decorrido: number): EstadoDaPartida {
  const total = p.resultado.totalMinutos;
  const fim1 = p.resultado.fimPrimeiroTempo;
  if (decorrido >= l.fim) return { minuto: total, fase: 'fim' };
  const m = minutoEm(l, decorrido);
  if (m === 0) return { minuto: 0, fase: 'ao-vivo' };
  const off = decorrido - (l.inicios[m] ?? 0);
  if (m > total) return { minuto: total, fase: 'fim' };

  const disputa = disputaDaPartida(e, rodada, p.mandante, p.visitante);
  if (m === total) {
    if (disputa?.cobrancas && off < disputa.cobrancas.length * COBRANCA_MS + 800) {
      const k = Math.min(disputa.cobrancas.length - 1, Math.floor(off / COBRANCA_MS));
      const noChute = off - k * COBRANCA_MS;
      const etapa = noChute < COBRANCA_TENSAO_MS ? 'tensao' : 'resultado';
      return { minuto: total, fase: 'penaltis', disputa: { reveladas: etapa === 'tensao' ? k : k + 1, etapa } };
    }
    return { minuto: total, fase: 'fim' };
  }
  if (p.resultado.eventos.some((ev) => ev.tipo === 'penalti' && ev.minuto === m)) {
    if (off < PENALTI_TENSAO_MS) return { minuto: m, fase: 'ao-vivo', suspense: { minuto: m, etapa: 'tensao' } };
    if (off < PENALTI_TENSAO_MS + PENALTI_RESULTADO_MS) return { minuto: m, fase: 'ao-vivo', suspense: { minuto: m, etapa: 'resultado' } };
  }
  return { minuto: m, fase: m === fim1 ? 'intervalo' : 'ao-vivo' };
}
